<template>
  <div class="detail-score-panel" :class="{ 'is-readonly': readonly }">
    <nav class="subject-nav" aria-label="评估维度">
      <button
        v-for="subject in subjects"
        :key="subject.key"
        type="button"
        class="subject-nav-item"
        :class="{ active: activeKey === subject.key }"
        @click="activeKey = subject.key"
      >
        <span>{{ subject.name }}</span>
        <strong>{{ subjectTotal(subject.key) }}分</strong>
      </button>
    </nav>
    <div class="subject-content">
      <header class="subject-header">
        <div>
          <div class="subject-name">{{ activeSubject.name }}</div>
          <div class="subject-count">共 {{ activeSubject.count }} 题，每题 {{ scoreRangeText }}</div>
        </div>
        <div class="subject-total">合计 <strong>{{ subjectTotal(activeKey) }}</strong> 分</div>
      </header>
      <div class="question-grid">
        <div v-for="(column, columnIndex) in questionColumns" :key="columnIndex" class="question-column">
          <div v-for="index in column" :key="index" class="question-item">
            <span class="question-number">第 {{ index }} 题</span>
            <span v-if="readonly" class="readonly-score">{{ questionValue(activeKey, index - 1) }}</span>
            <a-radio-group
              v-else
              :value="questionValue(activeKey, index - 1)"
              button-style="solid"
              size="small"
              @change="changeScore(index - 1, $event.target.value)"
            >
              <a-radio-button v-for="option in scoreOptions" :key="option" :value="option">
                {{ option }}
              </a-radio-button>
            </a-radio-group>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const SUBJECTS = [
  { key: 'se', name: '社会-情绪(SE)', count: 35, maxScore: 5 },
];

export default {
  name: 'BayleyScoreDetail',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeKey: SUBJECTS[0].key,
    };
  },
  computed: {
    subjects() {
      return SUBJECTS;
    },
    activeSubject() {
      return this.subjects.find((subject) => subject.key === this.activeKey) || this.subjects[0];
    },
    scoreOptions() {
      return Array.from({ length: this.activeSubject.maxScore + 1 }, (_, index) => index);
    },
    scoreRangeText() {
      return this.scoreOptions.join('、');
    },
    questionColumns() {
      const columnCount = this.readonly ? 3 : 2;
      const columnSize = Math.ceil(this.activeSubject.count / columnCount);
      return Array.from({ length: columnCount }, (_, columnIndex) => {
        const start = columnIndex * columnSize + 1;
        const end = Math.min(start + columnSize - 1, this.activeSubject.count);
        return Array.from({ length: Math.max(end - start + 1, 0) }, (__, index) => start + index);
      }).filter((column) => column.length);
    },
  },
  watch: {
    value: {
      handler() {
        this.$emit('totals-change', this.calculateTotals(this.normalizedScores()));
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    if (!this.readonly) this.$emit('input', this.normalizedScores());
  },
  methods: {
    normalizedScores() {
      const normalized = {};
      this.subjects.forEach((subject) => {
        const values = Array.isArray(this.value && this.value[subject.key]) ? this.value[subject.key] : [];
        normalized[subject.key] = Array.from({ length: subject.count }, (_, index) => {
          const value = Number(values[index]);
          return Number.isInteger(value) && value >= 0 && value <= subject.maxScore ? value : 0;
        });
      });
      return normalized;
    },
    questionValue(subject, index) {
      return this.normalizedScores()[subject][index];
    },
    changeScore(index, value) {
      const scores = this.normalizedScores();
      this.$set(scores[this.activeKey], index, Number(value));
      this.$emit('input', scores);
      this.$emit('totals-change', this.calculateTotals(scores));
    },
    subjectTotal(subject) {
      return this.normalizedScores()[subject].reduce((total, score) => total + score, 0);
    },
    calculateTotals(scores) {
      return this.subjects.reduce((totals, subject) => {
        totals[`score_${subject.key}`] = scores[subject.key].reduce((total, score) => total + score, 0);
        return totals;
      }, {});
    },
  },
};
</script>

<style scoped>
.detail-score-panel {
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  min-height: 360px;
  margin: 24px 0 34px;
  overflow: hidden;
  border: 1px solid #f7ced8;
  border-radius: 6px;
  background: #fff;
}

.subject-nav {
  padding: 10px;
  border-right: 1px solid #f7ced8;
  background: #faf7fa;
}

.subject-nav-item {
  display: flex;
  width: 100%;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  border: 0;
  border-radius: 4px;
  color: #555;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.subject-nav-item strong {
  flex: 0 0 auto;
  color: #999;
  font-size: 12px;
}

.subject-nav-item.active {
  color: #DC204F;
  background: #fbe4ea;
  font-weight: 600;
}

.subject-nav-item.active strong {
  color: #DC204F;
}

.subject-content {
  min-width: 0;
  padding: 20px 22px 24px;
}

.subject-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}

.subject-name {
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.subject-count {
  margin-top: 3px;
  color: #999;
  font-size: 12px;
}

.subject-total {
  color: #666;
}

.subject-total strong {
  color: #DC204F;
  font-size: 20px;
}

.question-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(250px, 1fr));
  gap: 18px;
  padding-top: 18px;
}

.detail-score-panel.is-readonly .question-grid {
  grid-template-columns: repeat(3, minmax(150px, 1fr));
}

.question-column {
  display: grid;
  align-content: start;
  gap: 10px;
}

.question-item {
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 4px 12px;
  border-bottom: 1px solid #f1f1f1;
}

.question-number {
  color: #666;
  font-size: 13px;
}

.readonly-score {
  display: grid;
  width: 34px;
  height: 28px;
  place-items: center;
  border-radius: 4px;
  color: #DC204F;
  background: #fbe4ea;
  font-weight: 600;
}

@media (max-width: 900px) {
  .detail-score-panel {
    grid-template-columns: 1fr;
  }

  .subject-nav {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-right: 0;
    border-bottom: 1px solid #f7ced8;
  }

  .question-grid {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 560px) {
  .subject-nav,
  .question-grid,
  .detail-score-panel.is-readonly .question-grid {
    grid-template-columns: 1fr;
  }
}
</style>
